import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";

// Shape-to-object mapping
const shapeObjectMap: { [key: string]: string[] } = {
    "circle.png": ["sun.png", "moon.png", "clock.png", "wheel.png", "ball.png"],
    "square.png": ["chessboard.png", "breadslice.png", "window.png", "photoframe.png", "wallclocksquare.png"],
    "triangle.png": ["pizzaslice.png", "trafficsign.png", "walermelonpiece.png", "sandwitchpiece.png", "christmastree.png"],
    "star.png": ["starfish.png", "starpendent.png", "starballoon.png", "starcookies.png", "starglasses.png"],
    "diamond.png": ["kite.png", "playingcard.png", "diamondbiscuit.png", "diamondbutton.png", "diamondearing.png"],
    "rectangle.png": ["mobilephone.png", "door.png", "rectangleframe.png", "television.png", "rectangleblackboard.png"],
    "pentagon.png": ["house.png", "kennel.png", "pentagontrafficsign.png", "pentagonbutton.png", "pentagonkeyring.png"],
};

interface ImagePair {
    left_id: number;
    right_id: number;
    leftObjects: string;
    rightObjects: string;
}

// Function to shuffle an array
const shuffleArray = (array: any[]) => array.sort(() => Math.random() - 0.5);

const MatchShapesWithObjects: React.FC = () => {
    const [questions, setQuestions] = useState<ImagePair[]>([]);
    const [connections, setConnections] = useState<{ x1: number; y1: number; x2: number; y2: number; correct: boolean }[]>([]);
    const [selectedShape, setSelectedShape] = useState<{ id: number; x: number; y: number } | null>(null);
    const [selectedLeft, setSelectedLeft] = useState<{ id: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [height, setHeight] = useState('0px');
    const [showImage, setShowImage] = useState(false);

    const modal = useRef<HTMLIonModalElement>(null);

    // Function to generate dynamic questions
    const generateQuestions = () => {
        const shuffledShapes = shuffleArray(Object.keys(shapeObjectMap)).slice(0, 4); // Pick 4 random shapes
        const newObjects = shuffledShapes.map((shape, index) => ({
            id: index + 1,
            shape,
            object: shapeObjectMap[shape][Math.floor(Math.random() * shapeObjectMap[shape].length)], // Pick a random object from the values
        }));

        const suffledobject = shuffleArray([...newObjects]);
        const newQuestions: ImagePair[] = [];
        newObjects.map((object, index) => {
            newQuestions.push({
                left_id: index + 1,
                right_id: newObjects.indexOf(suffledobject[index]) + 1,
                leftObjects: object.shape,
                rightObjects: suffledobject[index].object,
            });
        })

        console.log(newQuestions);

        setQuestions(newQuestions);
        setConnections([]);
        setSelectedShape(null);
    };

    useEffect(() => {
        generateQuestions();
        document.title = "Mamas Guidance - Nursery - English Language - Match Shapes With Object"; // Set page title
    }, []);

    // Get position relative to container
    const getRelativePosition = (element: HTMLDivElement) => {
        const container = containerRef.current;
        if (!container) return { x: 0, y: 0 };

        const elementRect = element.getBoundingClientRect();
        setHeight(container.scrollHeight + 'px');

        const screenWidth = window.innerWidth;
        let elementX, elementY;
        if (screenWidth >= 1024) {
            elementX = element.offsetLeft + elementRect.width;
            elementY = element.offsetTop + elementRect.height / 2;
        }
        else {
            elementX = element.offsetLeft + elementRect.width / 2;
            elementY = element.offsetTop + elementRect.height / 2;
        }

        return {
            x: elementX,
            y: elementY
        }
    };

    // Handle shape click
    const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>, id: number) => {
        const position = getRelativePosition(event.currentTarget);
        setSelectedShape({ id, x: position.x, y: position.y });
    };

    // Handle object click
    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, rightId: number) => {
        if (selectedShape) {
            const position = getRelativePosition(event.currentTarget);
            const isCorrect = selectedShape.id === rightId;

            setConnections([...connections, { x1: selectedShape.x, y1: selectedShape.y, x2: position.x, y2: position.y, correct: isCorrect }]);
            setSelectedShape(null);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Teach shape recognition with the "Match Shapes with Object" activity on Mamas Guidance—an engaging exercise for nursery kids to connect basic shapes with real-life objects, building early geometry and observation skills.' />
            </Helmet>
            {!showImage ? (
                <>
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Match Shapes with Objects</h1>

                            <p>
                                Let&apos;s make learning shapes exciting! This <strong>Match Shapes with Objects</strong> worksheet helps young learners connect basic 2D shapes with everyday objects. It&apos;s a visual and hands-on activity that strengthens <strong>early geometry skills</strong> in a fun and interactive way.
                            </p>

                            <h2>🔍 What&apos;s in This Worksheet?</h2>
                            <p>
                                Children are shown shapes such as circles, squares, rectangles, triangles, and ovals on one side, and matching real-life objects on the other. Their task is to <strong>draw lines from each shape to the object</strong> that resembles it — like matching a circle to a clock or a triangle to a slice of pizza.
                            </p>

                            <h2>🎯 What Will Your Child Learn?</h2>
                            <ul>
                                <li>Recognize common <strong>2D shapes</strong></li>
                                <li>Identify shapes in everyday items</li>
                                <li>Enhance visual discrimination skills</li>
                                <li>Develop <strong>logical thinking</strong> through matching</li>
                            </ul>

                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/nursery/mathematics/count-object"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Count Object</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/mathematics/missing-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Missing Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/mathematics/bigger-or-smaller"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Bigger Or Smaller</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why Shape Recognition Matters</h2>
                            <p>
                                Shape recognition is a foundation for early math and reading skills. It supports <strong>spatial awareness</strong>, <strong>pattern recognition</strong>, and even early writing. When kids link shapes to <strong>real-world objects</strong>, they begin to observe patterns in their environment.
                            </p>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Match Shapes with Objects</h1>

                        <p>
                            Let&apos;s make learning shapes exciting! This <strong>Match Shapes with Objects</strong> worksheet helps young learners connect basic 2D shapes with everyday objects. It&apos;s a visual and hands-on activity that strengthens <strong>early geometry skills</strong> in a fun and interactive way.
                        </p>

                        <h2>🔍 What&apos;s in This Worksheet?</h2>
                        <p>
                            Children are shown shapes such as circles, squares, rectangles, triangles, and ovals on one side, and matching real-life objects on the other. Their task is to <strong>draw lines from each shape to the object</strong> that resembles it — like matching a circle to a clock or a triangle to a slice of pizza.
                        </p>

                        <h2>🎯 What Will Your Child Learn?</h2>
                        <ul>
                            <li>Recognize common 2D shapes</li>
                            <li>Identify shapes in everyday items</li>
                            <li>Enhance visual discrimination skills</li>
                            <li>Develop logical thinking through matching</li>
                        </ul>

                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/nursery/mathematics/count-object"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Count Object</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/nursery/mathematics/missing-number"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Missing Number</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/nursery/mathematics/bigger-or-smaller"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Bigger Or Smaller</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🧠 Why Shape Recognition Matters</h2>
                        <p>
                            Shape recognition is a foundation for early math and reading skills. It supports <strong>spatial awareness</strong>, <strong>pattern recognition</strong>, and even early writing. When kids link shapes to <strong>real-world objects</strong>, they begin to observe patterns in their environment.
                        </p>

                    </article>
                    <div ref={containerRef} className="contentPart additionWord matchObject match-container" style={{ position: "relative", height: "calc(-200px + 100vh)", marginTop: "0%", paddingTop: "0%" }}>
                        {/* SVG for connections */}
                        <svg ref={svgRef} className="connector-svg" height={height} style={{ position: "absolute", top: 0, left: 0, width: "100%", pointerEvents: "none", zIndex: 10 }}>
                            {connections.map((conn, index) => (
                                <line key={index} x1={conn.x1} y1={conn.y1} x2={conn.x2} y2={conn.y2} stroke={conn.correct ? "green" : "red"} strokeWidth="3" />
                            ))}
                        </svg>

                        {/* <h6>Match the Shapes with Objects</h6> */}

                        <div className="columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%" }}>
                            {/* Left Column - Shapes */}
                            <div className="mb-2 columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%" }}>
                                <div className="left-column">
                                    {questions.map((item) => (
                                        <div key={item.left_id} className={`image-box ${selectedShape?.id === item.left_id ? "selected" : ""}`} onClick={(e) => handleLeftClick(e, item.left_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                            <img src={process.env.REACT_APP_API_URL + item.leftObjects} />
                                        </div>
                                    ))}
                                </div>
                                <div className="right-column">
                                    {questions.map((item) => (
                                        <div key={item.right_id} className="image-box" onClick={(e) => handleRightClick(e, item.right_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                            <img src={process.env.REACT_APP_API_URL + item.rightObjects} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Controls */}
                        <div className="text-center">
                            <button className="btn hero-btn" onClick={generateQuestions}>Next &gt;&gt;</button>
                        </div>
                    </div>
                </>
            ) : (
                <div className="contentPart showMiddle">
                    <img style={{ height: "150px" }} className="img-fluid" src={process.env.REACT_APP_LOADING_LINK} />
                </div>
            )}
        </>
    );
};

export default MatchShapesWithObjects;
